import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Storefront, Cube, ForkKnife, Car, Umbrella, ShieldCheck, Bank, Gavel, MapPinLine } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";

const DIFERENCIAIS = [
  { icon: Umbrella, title: "Shopping Coberto", text: "Conforto em qualquer clima, o ano todo." },
  { icon: ShieldCheck, title: "Segurança 24h", text: "Segurança humana e monitoramento ininterrupto." },
  { icon: Bank, title: "Arrecadação Municipal", text: "Posto de arrecadação municipal no local." },
  { icon: Gavel, title: "148ª Zona Eleitoral", text: "Cartório Eleitoral do TRE-RJ no shopping." },
  { icon: MapPinLine, title: "Localização Central", text: "No coração de Piabetá, fácil acesso e alto fluxo." },
  { icon: Car, title: "Estacionamentos Próximos", text: "Boa Sorte e Rio Sul — opções pagas e externas ao lado." },
];

const CHAPTERS = [
  { n: "01", icon: Storefront, title: "35 Lojas & Âncoras", text: "Operações satélites e âncoras que reúnem tudo o que o seu dia pede, lado a lado." },
  { n: "02", icon: Cube, title: "Estandes Inteligentes (Contêineres)", text: "Estandes inteligentes em contêineres — negócios ágeis, modernos e cheios de personalidade." },
  { n: "03", icon: ForkKnife, title: "Âncora Gastronômica", text: "O Kaven Restaurante ancora a experiência com alta gastronomia e ambiente sofisticado." },
  { n: "04", icon: Car, title: "Estacionamento Farto", text: "Chegue, estacione com tranquilidade e resolva tudo sem complicação." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Positioning() {
  const { positioning_paragraph, container_text } = useContent();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="posicionamento" ref={ref} className="relative bg-vm-bg py-24 md:py-32" data-testid="positioning-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">
              Posicionamento
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="positioning-title">
              Seu negócio merece um <span className="gold-text italic">endereço melhor.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative inline-block">
              <span className="font-serif text-[7rem] sm:text-[9rem] leading-none gold-text" data-testid="positioning-stat-65">65</span>
              <span className="block -mt-4 text-sm tracking-[0.25em] uppercase text-vm-mutedFg">Operações no Street Mall</span>
            </motion.div>
          </div>
        </div>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }} className="mt-10 max-w-3xl text-lg text-vm-mutedFg leading-relaxed" data-testid="positioning-paragraph">
          {positioning_paragraph}
        </motion.p>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="diferenciais-grid">
          {DIFERENCIAIS.map((d, i) => (
            <motion.div
              key={d.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="group flex items-start gap-4 rounded-xl border border-vm-border bg-vm-surface p-5 hover:border-vm-gold/40 transition-colors duration-500"
              data-testid={`diferencial-${i}`}
            >
              <d.icon size={28} weight="duotone" className="text-vm-gold shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-medium mb-1">{d.title}</h3>
                <p className="text-sm text-vm-mutedFg leading-relaxed">{d.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-vm-border rounded-2xl overflow-hidden border border-vm-border">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="group bg-vm-surface p-8 hover:bg-vm-muted transition-colors duration-500"
              data-testid={`positioning-chapter-${c.n}`}
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-serif text-sm text-vm-mutedFg group-hover:text-vm-gold transition-colors">{c.n}</span>
                <c.icon size={26} weight="duotone" className="text-vm-gold" />
              </div>
              <h3 className="font-serif text-xl mb-3">{c.title}</h3>
              <p className="text-sm text-vm-mutedFg leading-relaxed">{c.n === "02" ? container_text : c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
